import Analytics from "analytics";
import googleAnalyticsPlugin from "@analytics/google-analytics";
import { history } from "./router.js";

// Initialize analytics and plugins
// Documentation: https://getanalytics.io
const analytics = Analytics({
  debug: process.env.NODE_ENV !== "production",
  plugins: [
    googleAnalyticsPlugin({
      measurementIds: [process.env.REACT_APP_GA_MEASUREMENT_ID],
    }),
  ],
});

// Track initial pageview
if (typeof window !== "undefined") {
  analytics.page();
}

// Track pageview on route change
history.listen(() => {
  analytics.page();
});

// Event tracking helper functions
// These provide consistent event naming and structure across the app

// Authentication Events
export const trackSignupAttempt = (method = 'email') => {
  analytics.track('signup_attempt', {
    category: 'authentication',
    method,
  });
};

export const trackSignupSuccess = (method = 'email', userId) => {
  analytics.track('signup_success', {
    category: 'authentication',
    method,
    user_id: userId,
  });
};

export const trackSignupError = (method = 'email', error) => {
  analytics.track('signup_error', {
    category: 'authentication',
    method,
    error_message: error,
  });
};

export const trackSigninAttempt = (method = 'email') => {
  analytics.track('signin_attempt', {
    category: 'authentication',
    method,
  });
};

export const trackSigninSuccess = (method = 'email', userId) => {
  analytics.track('signin_success', {
    category: 'authentication',
    method,
    user_id: userId,
  });
};

export const trackSigninError = (method = 'email', error) => {
  analytics.track('signin_error', {
    category: 'authentication',
    method,
    error_message: error,
  });
};

export const trackLogout = (userId) => {
  analytics.track('logout', {
    category: 'authentication',
    user_id: userId,
  });
};

export const trackPasswordResetRequest = () => {
  analytics.track('password_reset_request', {
    category: 'authentication',
  });
};

export const trackPasswordResetSuccess = () => {
  analytics.track('password_reset_success', {
    category: 'authentication',
  });
};

// Pricing & Purchase Events
export const trackPlanViewed = (planId, planName, price) => {
  analytics.track('plan_viewed', {
    category: 'pricing',
    plan_id: planId,
    plan_name: planName,
    price,
  });
};

export const trackPlanSelected = (planId, planName, price) => {
  analytics.track('plan_selected', {
    category: 'pricing',
    plan_id: planId,
    plan_name: planName,
    price,
  });
};

export const trackCheckoutInitiated = (planId, planName, price) => {
  analytics.track('checkout_initiated', {
    category: 'purchase',
    plan_id: planId,
    plan_name: planName,
    price,
  });
};

export const trackPurchaseCompleted = (planId, planName, price, transactionId) => {
  analytics.track('purchase_completed', {
    category: 'purchase',
    plan_id: planId,
    plan_name: planName,
    price,
    transaction_id: transactionId,
  });
};

export const trackPurchaseError = (planId, error) => {
  analytics.track('purchase_error', {
    category: 'purchase',
    plan_id: planId,
    error_message: error,
  });
};

export const trackFreeTrialStarted = (planId, planName) => {
  analytics.track('free_trial_started', {
    category: 'purchase',
    plan_id: planId,
    plan_name: planName,
  });
};

// Product Usage Events
export const trackImageUpload = (imageCount = 1, source = 'unknown') => {
  analytics.track('image_upload', {
    category: 'product_usage',
    image_count: imageCount,
    source,
  });
};

export const trackImageUploadError = (error) => {
  analytics.track('image_upload_error', {
    category: 'product_usage',
    error_message: error,
  });
};

export const trackImageProcessingStarted = (furnitureType, designStyle) => {
  analytics.track('image_processing_started', {
    category: 'product_usage',
    furniture_type: furnitureType,
    design_style: designStyle,
  });
};

export const trackImageProcessingCompleted = (furnitureType, designStyle, processingTime) => {
  analytics.track('image_processing_completed', {
    category: 'product_usage',
    furniture_type: furnitureType,
    design_style: designStyle,
    processing_time_ms: processingTime,
  });
};

export const trackImageProcessingError = (error) => {
  analytics.track('image_processing_error', {
    category: 'product_usage',
    error_message: error,
  });
};

export const trackDesignStyleSelected = (style) => {
  analytics.track('design_style_selected', {
    category: 'product_usage',
    style,
  });
};

export const trackFurnitureTypeSelected = (type) => {
  analytics.track('furniture_type_selected', {
    category: 'product_usage',
    type,
  });
};

export const trackCreditUsed = (creditsUsed, creditsRemaining) => {
  analytics.track('credit_used', {
    category: 'product_usage',
    credits_used: creditsUsed,
    credits_remaining: creditsRemaining,
  });
};

export const trackImageDownload = (imageId) => {
  analytics.track('image_download', {
    category: 'product_usage',
    image_id: imageId,
  });
};

export const trackFeedbackSubmitted = (feedbackType, context) => {
  analytics.track('feedback_submitted', {
    category: 'product_usage',
    feedback_type: feedbackType,
    context,
  });
};

// Navigation & CTA Events
export const trackCTAClick = (ctaName, location, destination) => {
  analytics.track('cta_click', {
    category: 'navigation',
    cta_name: ctaName,
    location,
    destination,
  });
};

export const trackNavigationClick = (linkName, destination) => {
  analytics.track('navigation_click', {
    category: 'navigation',
    link_name: linkName,
    destination,
  });
};

export const trackFeatureAccess = (featureName, isFirstTime = false) => {
  analytics.track('feature_access', {
    category: 'navigation',
    feature_name: featureName,
    is_first_time: isFirstTime,
  });
};

// Error & Form Events
export const trackFormValidationError = (formName, fieldName, errorMessage) => {
  analytics.track('form_validation_error', {
    category: 'error',
    form_name: formName,
    field_name: fieldName,
    error_message: errorMessage,
  });
};

export const trackAPIError = (endpoint, method, statusCode, errorMessage) => {
  analytics.track('api_error', {
    category: 'error',
    endpoint,
    method,
    status_code: statusCode,
    error_message: errorMessage,
  });
};

export const trackFormSubmission = (formName) => {
  analytics.track('form_submission', {
    category: 'engagement',
    form_name: formName,
  });
};

// Settings & Configuration Events
export const trackSettingChanged = (settingName, newValue) => {
  analytics.track('setting_changed', {
    category: 'settings',
    setting_name: settingName,
    new_value: newValue,
  });
};

// Marketplace Events
export const trackMarketplaceItemViewed = (itemId, itemName) => {
  analytics.track('marketplace_item_viewed', {
    category: 'marketplace',
    item_id: itemId,
    item_name: itemName,
  });
};

export const trackMarketplaceItemClicked = (itemId, itemName) => {
  analytics.track('marketplace_item_clicked', {
    category: 'marketplace',
    item_id: itemId,
    item_name: itemName,
  });
};

export const trackSearchPerformed = (searchTerm, resultsCount, location) => {
  analytics.track('search_performed', {
    category: 'engagement',
    search_term: searchTerm,
    results_count: resultsCount,
    location,
  });
};

export default analytics;
